// WIDGET — Controls Yet to be Assessed: bar chart ranked by SSPs still pending, drill-down to per-control detail.
// Self-fetching: calls the dedicated GET /dashboard/grc/system-owner/unassessed-controls
// endpoint directly (not the shared applications-ssps rollup used by the other KRI
// widgets) so its own Application/SSP scope filters can re-fire independently. The
// Application filter's own options come from GET /users/current/accessible-organizations
// ?org_type=APPLICATION (fetchAccessibleApplications) — SSPs aren't in that response, so
// the SSP filter stays empty until a per-application SSP source is wired up.
import { useCallback, useEffect, useState } from "react";
import { ArrowRight, AlertTriangle, ChevronLeft } from "lucide-react";
import { Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Cell, LabelList } from "recharts";
import { Card, SectionTitle } from "../../GRCManagerDashboard/grcDashboardWidgets";
import { DataTable, type Column } from "../../../components/DataTable";
import { Dropdown } from "../../../components/Dropdown";
import { Sheet, SheetContent } from "../../../components/ui/sheet";
import { EmptyState } from "../../../components/EmptyState";
import { SkeletonList } from "../../../components/Skeleton";
import { STATUS_HEX, type AppSspOption } from "../data/applicationKriData";
import {
  fetchUnassessedControls,
  fetchAccessibleApplications,
  type UnassessedControlRow,
  type UnassessedControlsResponse,
  type UnassessedPlanRef,
} from "../../../../api/frameworksApi";

/* Every KRI widget card is this tall — matched to the Organization view's
   cards (a 320px chart body plus header and card padding) so both tabs read
   as the same size. */
const KRI_CARD = "flex h-[520px] flex-col";

function WidgetHeader({ title, subtitle, onViewMore }: { title: string; subtitle?: string; onViewMore?: () => void }) {
  return (
    <div className="mb-3 flex items-start justify-between gap-2">
      <SectionTitle title={title} subtitle={subtitle} />
      {onViewMore && (
        <button
          onClick={onViewMore}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-indigo-500/50 px-2.5 py-1 text-xs font-semibold text-foreground transition-colors hover:border-indigo-500 hover:bg-indigo-500/10"
        >
          View More <ArrowRight className="size-3.5" />
        </button>
      )}
    </div>
  );
}

/** planTitle can be null if the plan's document title wasn't set — fall back
 *  to the last 8 characters of the plan id. */
function planLabel(plan: { planId: string; planTitle: string | null }): string {
  return plan.planTitle || plan.planId.slice(-8);
}

/** The SSP a plan belongs to. sspName is null when the SSP isn't visible to the
 *  caller or has no document title, so fall back to the sspId short form; both
 *  can be null (unparseable importSspHref), in which case there's nothing to show. */
function sspLabel(plan: UnassessedPlanRef): string | null {
  return plan.sspName || (plan.sspId ? plan.sspId.slice(-8) : null);
}

/* ══ Controls Yet to be Assessed — drill-down drawer ══════════════════════ */
function PendingControlDrawer({
  open,
  onOpenChange,
  rows,
  selectedId,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  rows: UnassessedControlRow[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const detail = selectedId ? (rows.find((r) => r.controlId === selectedId) ?? null) : null;

  const listColumns: Column<UnassessedControlRow>[] = [
    {
      key: "controlId",
      header: "Control",
      align: "left",
      headerAlign: "left",
      render: (r) => <span className="text-sm font-bold text-foreground">{r.controlId}</span>,
    },
    {
      key: "controlTitle",
      header: "Control Title",
      align: "left",
      headerAlign: "left",
      render: (r) =>
        r.controlTitle ? (
          <span className="text-sm text-foreground/90">{r.controlTitle}</span>
        ) : (
          <span className="text-xs text-muted-foreground">Not in catalog</span>
        ),
    },
    {
      key: "sspCount",
      header: "Pending In",
      render: (r) => (
        <span
          className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-bold tabular-nums"
          style={{ background: `${STATUS_HEX.warning}1f`, color: STATUS_HEX.warning }}
        >
          {r.sspCount} SSP{r.sspCount === 1 ? "" : "s"}
        </span>
      ),
    },
    {
      key: "planCount",
      header: "Plans",
      render: (r) => (
        <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-bold tabular-nums text-foreground/80">
          {r.planCount} plan{r.planCount === 1 ? "" : "s"}
        </span>
      ),
    },
    {
      key: "missingFromPlans",
      header: "Pending Assessment Names",
      align: "left",
      headerAlign: "left",
      render: (r) =>
        r.missingFromPlans.length ? (
          <div className="flex flex-wrap gap-1.5">
            {r.missingFromPlans.map((p) => (
              <span key={p.planId} className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80">
                {planLabel(p)}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">None</span>
        ),
    },
  ];

  return (
    <Sheet open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) onSelect(null); }}>
      <SheetContent side="right" className="flex h-full max-h-screen w-[96vw] flex-col gap-0 overflow-hidden border-l border-border bg-background p-0 sm:max-w-[1100px]">
        {detail ? (
          <div key={`detail-${selectedId}`} className="flex h-full min-h-0 flex-1 flex-col animate-in fade-in-0 slide-in-from-right-4 duration-300 ease-out">
            <div className="shrink-0 border-b border-border p-6" style={{ borderLeftWidth: 3, borderLeftColor: STATUS_HEX.warning }}>
              <button onClick={() => onSelect(null)} className="mb-3 flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground">
                <ChevronLeft className="size-4" /> Back to all controls
              </button>
              <div className="flex items-center gap-2">
                <AlertTriangle className="size-5 shrink-0" style={{ color: STATUS_HEX.warning }} />
                <h2 className="text-xl font-bold text-foreground">{selectedId}</h2>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{detail.controlTitle ?? "Not in catalog"}</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Not yet assessed in <b className="text-foreground">{detail.sspCount}</b> SSP{detail.sspCount === 1 ? "" : "s"}, across{" "}
                <b className="text-foreground">{detail.planCount}</b> plan{detail.planCount === 1 ? "" : "s"}.
              </p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-6">
              <div className="mb-4">
                <p className="text-sm font-semibold text-foreground">Assessment Required in the Following Plans</p>
                <p className="mt-1 text-xs text-muted-foreground">This control needs to be assessed in these plans to complete coverage</p>
              </div>
              {detail.missingFromPlans?.length ? (
                <DataTable
                  columns={[
                    {
                      key: "planName",
                      header: "Plan Name",
                      align: "left",
                      headerAlign: "left",
                      render: (p: UnassessedPlanRef) => (
                        <span className="text-sm font-medium text-foreground">{planLabel(p)}</span>
                      ),
                    },
                    {
                      key: "sspName",
                      header: "SSP",
                      align: "left",
                      headerAlign: "left",
                      render: (p: UnassessedPlanRef) => {
                        const ssp = sspLabel(p);
                        return ssp ? (
                          <span
                            className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium"
                            style={{ background: `${STATUS_HEX.warning}1f`, color: STATUS_HEX.warning }}
                          >
                            {ssp}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Unknown</span>
                        );
                      },
                    },
                  ]}
                  data={detail.missingFromPlans}
                  rowKey={(p) => p.planId}
                  emptyText="No plans exist yet"
                  hidePageButtons
                  compact
                />
              ) : (
                <p className="text-sm text-muted-foreground">No plans exist yet for the SSP(s) that declare this control.</p>
              )}
            </div>
          </div>
        ) : (
          <div key="list" className="flex h-full min-h-0 flex-1 flex-col animate-in fade-in-0 slide-in-from-left-4 duration-300 ease-out">
            <div className="shrink-0 border-b border-border p-6" style={{ borderLeftWidth: 3, borderLeftColor: STATUS_HEX.warning }}>
              <div className="flex items-center gap-2">
                <AlertTriangle className="size-5 shrink-0" style={{ color: STATUS_HEX.warning }} />
                <h2 className="text-xl font-bold text-foreground">Controls Yet to be Assessed</h2>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">All {rows.length} controls — select one for its per-plan detail</p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-6">
              <DataTable columns={listColumns} data={rows} rowKey={(r) => r.controlId} emptyText="No Data Available" onRowClick={(r) => onSelect(r.controlId)} hidePageButtons compact />
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function PendingControlTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: UnassessedControlRow }> }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-xl">
      <p className="font-semibold text-foreground">
        {p.controlId}
        {p.controlTitle ? ` — ${p.controlTitle}` : ""}
      </p>
      <p className="text-muted-foreground">
        Pending assessments: <span className="font-semibold text-foreground">{p.sspCount}</span>
      </p>
    </div>
  );
}

/* ══ Controls Yet to be Assessed — main export ═══════════════════════════
   Horizontal bar chart, ranked worst-first (server-sorted sspCount desc ->
   controlId asc — no client-side sort needed). No severity comes back from
   the API for these controls, so every bar shares one categorical color
   instead of a severity-coded one. ════════════════════════════════════ */
export function PendingAssessmentWidget() {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState<string | null>(null);
  const [filterAppId, setFilterAppId] = useState("");
  const [filterSspId, setFilterSspId] = useState("");
  const [data, setData] = useState<UnassessedControlsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appOptions, setAppOptions] = useState<AppSspOption[]>([]);

  // Application filter options — fetched once; independent of the unassessed-controls
  // load below so a failure here never blocks the chart itself.
  useEffect(() => {
    let cancelled = false;
    fetchAccessibleApplications()
      .then((orgs) => {
        if (cancelled) return;
        setAppOptions(orgs.map((o) => ({ applicationId: o.id, applicationName: o.name, ssps: [] })));
      })
      .catch(() => {
        if (!cancelled) setAppOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchUnassessedControls({
      applicationId: filterAppId || undefined,
      sspId: filterSspId || undefined,
    })
      .then((res) => setData(res?.data ?? null))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load unassessed controls"))
      .finally(() => setLoading(false));
  }, [filterAppId, filterSspId]);

  // Re-fires whenever the Application/SSP scope changes — old data stays
  // visible (see the loading/error guards below) so the card never blanks
  // out mid-refetch.
  useEffect(() => {
    load();
  }, [load]);

  const sspOptionsForApp = filterAppId ? (appOptions.find((a) => a.applicationId === filterAppId)?.ssps ?? []) : [];
  const rows = data?.controls ?? [];
  const topRows = rows.slice(0, 5);
  const chartData = topRows.map((c) => ({ ...c, label: c.controlId }));
  const openControl = (id: string) => { setSel(id); setOpen(true); };
  const barHex = STATUS_HEX.warning;

  const showSkeleton = loading && !data;
  const showError = !!error && !data;

  const scopeSubtitle =
    "Highlights pending control assessments across applications and SSPs to improve assessment coverage.";

  return (
    <Card className={KRI_CARD}>
      <WidgetHeader
        title="Controls Yet to be Assessed"
        subtitle={scopeSubtitle}
        onViewMore={rows.length ? () => { setSel(null); setOpen(true); } : undefined}
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Dropdown
          variant="pill"
          size="sm"
          className="min-w-[140px]"
          value={filterAppId}
          onChange={(v) => { setFilterAppId(v); setFilterSspId(""); }}
          checkboxStyle
          options={[
            { value: "", label: "All Applications" },
            ...appOptions.map((a) => ({ value: a.applicationId, label: a.applicationName })),
          ]}
        />
        {filterAppId && sspOptionsForApp.length > 0 && (
          <Dropdown
            variant="pill"
            size="sm"
            className="min-w-[140px]"
            value={filterSspId}
            onChange={setFilterSspId}
            options={[
              { value: "", label: "All SSPs" },
              ...sspOptionsForApp.map((s) => ({ value: s.sspId, label: s.sspName })),
            ]}
          />
        )}
      </div>

      <div className="flex min-h-0 flex-1 gap-4">
        <div className="min-h-0 w-full flex-1">
          {showSkeleton ? (
            <SkeletonList rows={5} />
          ) : showError ? (
            <EmptyState
              icon={AlertTriangle}
              title="Couldn't load this widget"
              description={error!}
              action={
                <button
                  onClick={load}
                  className="rounded-lg border border-indigo-500/50 px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-indigo-500 hover:bg-indigo-500/10"
                >
                  Retry
                </button>
              }
            />
          ) : rows.length === 0 ? (
            <EmptyState icon={AlertTriangle} title="Nothing pending" description="Every declared control has been assessed in at least one plan." />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 32, left: 0, bottom: 4 }}>
                <XAxis
                  type="number"
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: "currentColor" }}
                  className="text-foreground"
                  axisLine={{ stroke: "#94a3b8", strokeOpacity: 0.8 }}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={80}
                  tick={{ fontSize: 11, fill: "currentColor" }}
                  className="text-foreground"
                  axisLine={{ stroke: "#94a3b8", strokeOpacity: 0.8 }}
                  tickLine={false}
                />
                <Tooltip content={<PendingControlTooltip />} cursor={{ fill: "rgba(100,116,139,0.18)" }} />
                <Bar dataKey="sspCount" maxBarSize={22} radius={[0, 4, 4, 0]} cursor="pointer" onClick={(d: any) => d?.controlId && openControl(d.controlId)}>
                  <LabelList
                    dataKey="sspCount"
                    position="right"
                    content={(props: any) => {
                      const { x, y, width, height, value } = props;
                      return (
                        <text x={x + width + 6} y={y + height / 2} dy={4} fontSize={11} fontWeight={700} className="fill-foreground" style={{ pointerEvents: "none" }}>
                          {value}
                        </text>
                      );
                    }}
                  />
                  {chartData.map((d) => (
                    <Cell key={d.controlId} fill={`${barHex}26`} stroke={barHex} strokeWidth={1.5} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      <PendingControlDrawer open={open} onOpenChange={setOpen} rows={rows} selectedId={sel} onSelect={setSel} />
    </Card>
  );
}
